import { CartItem } from "@/types"

import { useAnimatedCurrency } from "@/hooks/use-animated-currency"
import { useOptimisticCart } from "@/hooks/use-optimistic-cart"
import {
  FLY_ORIGIN_ATTR,
  emitCartBump,
  flyToCart,
} from "@/lib/fly-to-cart"
import { TRANSITION } from "@/lib/motion"
import { AnimatePresence, motion } from "motion/react"
import { ImageWithFallback } from "../image/ImageWithFallback"
import DeleteItemModal from "./DeleteItemModal"
import { Price, QuantityStepper, Surface, Text } from "@/components/ui"
import { ItemNote, ItemOptionsList } from "@/components/orders/ItemOptionsList"
import { cartItemOptionLines } from "./cart-item-option-lines"

/** One bag line; `prizeDiscountCents` is what coupon-service takes off it for an attached prize, shown as the paid price next to the menu one. */
export default function CartItemRow({ cartItem, prizeDiscountCents = 0 }: { cartItem: CartItem; prizeDiscountCents?: number }) {
  return (
    <div className="flex gap-3">
      <Surface
        surface="card"
        className="h-16 w-16 shrink-0 self-start overflow-hidden rounded-card border border-border sm:h-20 sm:w-20"
        {...{ [FLY_ORIGIN_ATTR]: "" }}
      >
        <ImageWithFallback
          width={200}
          height={200}
          alt={cartItem.item.name}
          src={cartItem.item.image?.URL}
          crc={cartItem.item.image?.["CRC-32"]}
          fit="contain"
          className="h-full w-full"
        />
      </Surface>

      <div className="flex min-w-0 flex-1 flex-col">
        <Text weight="semibold" tone="default" leading="snug" clamp={2}>
          {cartItem.item.name}
        </Text>

        <ItemOptionsList options={cartItemOptionLines(cartItem)} className="mt-1" />

        <ItemNote note={cartItem.specialInstructions} className="mt-1" />

        <div className="mt-auto flex items-center justify-between gap-3 pt-2">
          <LinePrice cartItem={cartItem} prizeDiscountCents={prizeDiscountCents} />
          <ItemQuantityButton cartItem={cartItem} />
        </div>
      </div>
    </div>
  )
}

function LinePrice({ cartItem, prizeDiscountCents }: { cartItem: CartItem; prizeDiscountCents: number }) {
  const linePrice = useAnimatedCurrency(cartItem.itemTotalPrice)
  const discounted = prizeDiscountCents > 0
  const paidCents = Math.max(0, Math.round(cartItem.itemTotalPrice * 100) - prizeDiscountCents)

  return (
    <AnimatePresence initial={false} mode="wait">
      <motion.span
        key={discounted ? "prize" : "menu"}
        className="flex min-w-0 flex-col"
        initial={{ opacity: 0, y: 4 }}
        animate={{ opacity: 1, y: 0, transition: TRANSITION.fade }}
        exit={{ opacity: 0, y: -4, transition: TRANSITION.exit }}
      >
        {discounted ? (
          <>
            <Text as="span" size="xs" tone="subtle" leading="tight">
              <s>
                <span className="sr-only">Preço do cardápio </span>
                <motion.span className="tabular-nums">{linePrice}</motion.span>
              </s>
            </Text>
            <Text as="span" weight="bold" tone="secondary" leading="snug">
              <Price cents={paidCents} free />
              <span className="sr-only"> com o prêmio</span>
            </Text>
          </>
        ) : (
          <Text as="span" weight="bold" tone="secondary" leading="snug">
            <motion.span className="tabular-nums">{linePrice}</motion.span>
          </Text>
        )}
      </motion.span>
    </AnimatePresence>
  )
}

function ItemQuantityButton({ cartItem }: { cartItem: CartItem }) {
  const { updateItemQuantity } = useOptimisticCart()

  function increaseQuantity(event: React.MouseEvent<HTMLButtonElement>) {
    flyToCart(event.currentTarget.closest("li")?.querySelector(`[${FLY_ORIGIN_ATTR}]`))
    updateItemQuantity(cartItem.id, cartItem.quantity + 1)
  }

  function decreaseQuantity() {
    updateItemQuantity(cartItem.id, cartItem.quantity - 1)
    emitCartBump("remove")
  }

  return (
    <QuantityStepper
      value={cartItem.quantity}
      min={1}
      max={Infinity}
      tone="secondary"
      decrementTone="neutral"
      removeAtMin
      removeSlot={<DeleteItemModal cartItem={cartItem} />}
      bump
      onIncrement={increaseQuantity}
      onDecrement={decreaseQuantity}
    />
  )
}
